import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Chip, Typography } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { getAuthHeaders } from '../utils/authSession';
import { pamAPI } from '../services/api';
import { API_ROOT } from '../config/apiConfig';

type InboxRow = {
  id: string;
  kind: string;
  title: string;
  detail: string;
  path: string;
};

const PENDING = ['Pending Review', 'In Progress', 'Need More Info'];

export default function AdminInbox() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<InboxRow[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const next: InboxRow[] = [];
      try {
        const role = user?.role;
        if (role === 'super_admin' || role === 'sso_admin') {
          const res = await fetch(`${API_ROOT}/sso/configurations/enterprise`, { headers: getAuthHeaders() });
          if (res.ok) {
            const data = await res.json();
            const configs = (data.data || []) as Array<Record<string, string>>;
            configs
              .filter((config) => PENDING.includes(config.status) && (!config.assigned_to || config.assigned_to === user?.id))
              .slice(0, 8)
              .forEach((config) => {
                const due = new Date(config.created_at);
                due.setDate(due.getDate() + 7);
                next.push({
                  id: `sso-${config.id}`,
                  kind: 'SSO review',
                  title: config.application_name || 'SSO request',
                  detail: `${config.status} · ${config.assigned_to ? 'Assigned to you' : 'Unassigned'} · Due ${due.toLocaleDateString()}`,
                  path: `/sso-review?pipeline=pending&config=${config.id}`,
                });
              });
            configs
              .filter((config) => {
                if (!config.client_secret_expires_at) return false;
                const expires = new Date(config.client_secret_expires_at);
                if (Number.isNaN(expires.getTime())) return false;
                const days = (expires.getTime() - Date.now()) / 86400000;
                return days <= 30;
              })
              .slice(0, 5)
              .forEach((config) => {
                const expires = new Date(config.client_secret_expires_at);
                const days = Math.ceil((expires.getTime() - Date.now()) / 86400000);
                next.push({
                  id: `secret-${config.id}`,
                  kind: 'Secret expiry',
                  title: config.application_name || 'SSO secret',
                  detail:
                    days <= 14
                      ? `Secret expires in ${Math.max(days, 0)} days`
                      : `Secret expires ${expires.toLocaleDateString()}`,
                  path: `/sso-review?config=${config.id}`,
                });
              });
          }
        }
        if (role === 'super_admin' || role === 'pam_admin') {
          const accounts = await pamAPI.getAccounts();
          const list = (accounts.data.data || []) as Array<Record<string, string>>;
          list
            .filter((account) => account.status === 'expired' || (account.password_next_rotation && new Date(account.password_next_rotation).getTime() <= Date.now()))
            .slice(0, 5)
            .forEach((account) => {
              next.push({
                id: `pam-${account.id}`,
                kind: 'Privileged account',
                title: account.name,
                detail: `Overdue · ${account.owner_name || 'No owner'} · ${account.rotation_policy || 'Rotation policy'}`,
                path: '/pam-dashboard',
              });
            });
        }
        if (role === 'super_admin' || role === 'grc_admin') {
          next.push({
            id: 'risk-review',
            kind: 'Risk clarification',
            title: 'Risk assessments waiting for review',
            detail: 'Opens the review queue. Opening this row does not submit a decision.',
            path: '/risk-assessment-review',
          });
        }
        if (role === 'super_admin' || role === 'iga_admin') {
          next.push({
            id: 'ai-approvals',
            kind: 'AI approval',
            title: 'Pending AI access approvals',
            detail: 'Opens AI governance. Opening this row does not approve a grant.',
            path: '/ai-governance',
          });
        }
        if (!cancelled) setRows(next);
      } catch {
        if (!cancelled) setError('The inbox could not load every queue.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [user?.id, user?.role]);

  return (
    <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none', mb: 2 }}>
      <CardContent>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 0.5 }}>Inbox</Typography>
        <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mb: 1.5 }}>
          Assigned to you, or still unassigned in your role. Opening a row only opens the record.
        </Typography>
        {loading && <Typography sx={{ fontSize: '0.82rem' }}>Loading inbox…</Typography>}
        {error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && rows.length === 0 && <Alert severity="info">Nothing is waiting in your inbox.</Alert>}
        {rows.map((row) => (
          <Box key={row.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <Chip label={row.kind} size="small" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }} noWrap>{row.title}</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{row.detail}</Typography>
            </Box>
            <Button size="small" onClick={() => navigate(row.path)}>Open</Button>
          </Box>
        ))}
      </CardContent>
    </Card>
  );
}
